import { NgClass } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { ChartConfiguration, ChartData, ChartDataset } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { MoneyPipe, SHOW_DECIMALS } from '../../../pipes/framework/money-short.pipe';
import { CashFlowStatsComponent } from '../cash-flow-stats/cash-flow-stats.component';
import {
  CashflowData,
  CashflowGraph,
  ICashflowStatRow as CashflowStatRow,
} from '../cashflow-table/cashflow.types';
import { colors } from 'src/colors';

@Component({
  selector: 'app-cash-flow-graph',
  templateUrl: './cash-flow-graph.component.html',
  styleUrls: ['./cash-flow-graph.component.scss'],
  imports: [BaseChartDirective, CashFlowStatsComponent, NgClass],
})
export class CashFlowGraphComponent {
  isLoading = input(false);
  cashflowData = input<CashflowData | null>(null);

  private readonly DEFAULT_MAX_TICK = 1000;

  private readonly toShortMoney = inject(MoneyPipe);

  readonly cashflowStats = computed<CashflowStatRow[]>(() => {
    return this.cashflowData()?.stats ?? [];
  });

  readonly chartData = computed<ChartData<'bar'>>(() => {
    const graph = this.cashflowData()?.graph;
    return buildChartData(graph);
  });

  /**
   * Returns the chart configuration object.
   * Should be called whenever the data changes as it is dependent on the data (e.g. max tick value, min values).
   */
  readonly chartConfig = computed<ChartConfiguration<'bar'>['options']>(() => {
    const data = this.chartData();
    const maxTick = getMaxTickFromData(data, this.DEFAULT_MAX_TICK);
    const stepSize = Math.max(1, Math.round(maxTick / 4));

    const config: ChartConfiguration<'bar'>['options'] = {
      responsive: true,
      maintainAspectRatio: false,
      devicePixelRatio: 2,
      layout: {
        padding: { top: 20, left: 20 },
      },
      scales: {
        y: {
          display: true,
          stacked: false,
          max: maxTick,
          beginAtZero: true,
          ticks: {
            padding: 10,
            stepSize,
            color: colors['navy-900'],
            callback: (value) =>
              `\$${this.toShortMoney.transformToShortSimplified(
                this.toShortMoney.transformToShortSimplified(
                  value as number,
                  SHOW_DECIMALS.ABOVE_MILLION,
                ),
              )}`,
          },
          border: { display: false },
          grid: { color: colors['navy-400'] },
          afterFit: (axis) => {
            axis.paddingTop = 20;
            return axis;
          },
        },
        x: {
          display: false,
          stacked: true,
          suggestedMin: 0,
        },
      },
      plugins: {
        legend: { display: false },
      },
    };

    return config;
  });
}

/**
 * Returns the chart data with all of the datasets and their colors.
 */
function buildChartData(graph?: CashflowGraph | null): ChartData<'bar'> {
  // Fallbacks when no graph (keeps template stable)
  const fallbackColor = colors['navy-300'];
  const safeGraph = graph ?? {
    budgetCol: { data: Array(12).fill(0), color: fallbackColor },
    forecastCol: { data: Array(12).fill(0), color: fallbackColor },
    actualsCol: { data: Array(12).fill(0), color: fallbackColor },
  };

  const labels = Array(12).fill('');

  // negative values are not shown, so map them to 0
  const budget = safeGraph.budgetCol ? mapNegativeToZero(safeGraph.budgetCol.data) : null;
  const forecast = mapNegativeToZero(safeGraph.forecastCol.data);
  const actuals = mapNegativeToZero(safeGraph.actualsCol.data);

  const dataConf = {
    borderRadius: 4,
    categoryPercentage: 0.85,
    barPercentage: budget ? 0.8 : 0.4,
  };

  const datasets: ChartDataset<'bar', (number | [number, number])[]>[] = [
    {
      ...dataConf,
      data: forecast,
      label: '',
      stack: 'actuals_forecast',
      order: 2,
      backgroundColor: safeGraph.forecastCol.color,
    },
    {
      ...dataConf,
      data: actuals,
      label: '',
      stack: 'actuals_forecast',
      order: 1,
      backgroundColor: safeGraph.actualsCol.color,
    },
  ];
  if (budget) {
    datasets.push({
      ...dataConf,
      data: budget,
      label: '',
      stack: 'budget',
      backgroundColor: safeGraph.budgetCol.color,
    });
  }

  return {
    labels,
    datasets,
  };
}

/**
 * Based on the maximum of all the data, calculates the maximum tick value = highest line on the graph. <br/>
 * If there is no data, returns a default value (= DEFAULT_MAX_TICK).
 */
function getMaxTickFromData(data: ChartData<'bar'>, defaultMax: number): number {
  if (!data?.datasets?.length) return defaultMax;

  const values: number[] = [];
  for (const ds of data.datasets) {
    const arr = (ds.data as number[]) ?? [];
    for (const v of arr) values.push(typeof v === 'number' ? v : 0);
  }

  const maxValue = values.reduce((m, v) => (v > m ? v : m), Number.NEGATIVE_INFINITY);
  if (!isFinite(maxValue) || maxValue <= 0) return defaultMax;

  return maxValue * 1.17; // little headroom above the tallest bar
}

/**
 * Maps negative values of an array to 0
 */
function mapNegativeToZero(array: number[]): number[] {
  return array.map((v) => (v > 0 ? v : 0));
}
